<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-header">
        <div class="logo">
          <Gamepad2 size="28" />
          <span class="logo-text">星屿小筑</span>
        </div>
        <p class="login-subtitle">选择游戏端登录</p>
      </div>

      <div class="platform-selector" v-if="!selectedPlatform">
        <h3 class="selector-title">选择游戏端</h3>
        <div class="platform-grid">
          <div 
            class="platform-card"
            v-for="platform in platforms"
            :key="platform.key"
            :class="{ active: selectedPlatform === platform.key }"
            @click="selectPlatform(platform.key)"
          >
            <div class="platform-info">
              <span class="platform-name">{{ platform.name }}</span>
              <span class="platform-desc">{{ platform.desc }}</span>
            </div>
            <div class="platform-check" v-if="selectedPlatform === platform.key">
              <Check size="18" />
            </div>
          </div>
        </div>
      </div>

      <div class="login-form" v-else>
        <div class="back-btn" @click="selectedPlatform = null">
          <ArrowLeft size="18" />
          返回选择
        </div>
        
        <div class="platform-badge">
          <span class="badge-text">{{ currentPlatform?.name }}</span>
        </div>

        <h3 class="form-title">登录账户</h3>
        
        <div class="form-group">
          <label class="form-label">用户名</label>
          <input 
            type="text" 
            v-model="loginForm.username" 
            class="form-input"
            placeholder="请输入用户名"
            @keyup.enter="handleLogin"
          />
        </div>
        
        <div class="form-group">
          <label class="form-label">密码</label>
          <input 
            type="password" 
            v-model="loginForm.password" 
            class="form-input"
            placeholder="请输入密码"
            @keyup.enter="handleLogin"
          />
        </div>
        
        <button 
          class="login-btn" 
          @click="handleLogin"
          :disabled="loading"
        >
          <span v-if="loading">登录中...</span>
          <span v-else>登录</span>
        </button>
        
        <div class="login-register">
          <span>还没有账户？</span>
          <button class="register-link">联系管理员</button>
        </div>
      </div>

      <div class="login-footer">
        <p>星屿小筑 · 专业打手团队</p>
      </div>
    </div>

    <div class="toast" v-if="showToast" :class="toastType">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script setup>
/* eslint-disable vue/multi-word-component-names */
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowLeft, Check, Gamepad2 } from 'lucide-vue-next'
import { supabase, isFetchNetworkError } from '@/utils/supabase'
import { setSecureStorage, validateUsername, validatePassword, SECURE_STORAGE_KEY, PLATFORM_TABLE_MAP } from '@/utils/security'

const router = useRouter()

const platforms = [
  { key: 'mobile_delta', name: '手游三角洲', desc: '移动端三角洲行动' },
  { key: 'pc_delta', name: 'PC三角洲', desc: '电脑端三角洲行动' },
  { key: 'wzry', name: '王者荣耀', desc: '移动端MOBA游戏' },
  { key: 'hpjy', name: '和平精英', desc: '移动端射击游戏' }
]

const selectedPlatform = ref(null)
const loading = ref(false)
const loginForm = ref({
  username: '',
  password: ''
})

const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('')

const currentPlatform = computed(() => {
  return platforms.find(p => p.key === selectedPlatform.value)
})

function showToastMessage(message, type) {
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

function selectPlatform(key) {
  selectedPlatform.value = key
}

async function handleLogin() {
  if (loading.value) return
  
  const username = loginForm.value.username.trim()
  const password = loginForm.value.password
  
  const usernameValidation = validateUsername(username)
  if (!usernameValidation.valid) {
    showToastMessage(usernameValidation.message, 'error')
    return
  }
  
  const passwordValidation = validatePassword(password)
  if (!passwordValidation.valid) {
    showToastMessage(passwordValidation.message, 'error')
    return
  }

  loading.value = true
  try {
    const tableName = PLATFORM_TABLE_MAP[selectedPlatform.value].player
    const { data: users, error } = await supabase
      .from(tableName)
      .select('username, role')
      .eq('username', username)
      .eq('password', password)
      .limit(1)
    
    if (error) {
      console.error('查询用户失败:', error)
      const message = isFetchNetworkError(error)
        ? '连接服务器失败，请检查网络后重试'
        : '登录失败: 服务器异常，请稍后重试'
      showToastMessage(message, 'error')
      loading.value = false
      return
    }
    
    if (users && users.length > 0) {
      const userInfo = {
        username: users[0].username,
        role: users[0].role || '打手',
        platform: selectedPlatform.value
      }
      
      setSecureStorage(SECURE_STORAGE_KEY, userInfo)
      
      router.push('/mypage')
    } else {
      showToastMessage('登录失败: 用户名或密码错误', 'error')
    }
  } catch (error) {
    console.error('登录失败:', error)
    const message = isFetchNetworkError(error)
      ? '连接服务器失败，请检查网络后重试'
      : '登录失败: ' + error.message
    showToastMessage(message, 'error')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: var(--color-bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
}

.login-container {
  width: 100%;
  max-width: 400px;
  background: var(--color-bg-card);
  border-radius: var(--radius-xl);
  padding: 32px 24px;
  border: 1px solid var(--color-border);
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 8px;
  color: var(--color-accent-primary);
}

.logo-text {
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.login-subtitle {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
}

.platform-selector {
  margin-bottom: 24px;
}

.selector-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 16px;
  text-align: center;
}

.platform-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.platform-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.platform-card:hover {
  border-color: var(--color-border-hover);
  background: var(--color-bg-secondary);
}

.platform-card.active {
  border-color: var(--color-accent-primary);
  background: rgba(99, 102, 241, 0.08);
}

.platform-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.platform-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.platform-desc {
  font-size: 12px;
  color: var(--color-text-muted);
}

.platform-check {
  color: var(--color-accent-primary);
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--color-accent-primary);
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  width: fit-content;
}

.back-btn:hover {
  text-decoration: underline;
}

.platform-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: var(--radius-md);
  margin-bottom: 8px;
}

.badge-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-accent-primary);
}

.form-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  margin: 0 0 8px 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-size: 14px;
  transition: var(--transition-fast);
  box-sizing: border-box;
}

.form-input:focus {
  border-color: var(--color-accent-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  outline: none;
}

.login-btn {
  padding: 14px;
  background: var(--color-accent-gradient);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
  margin-top: 8px;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-register {
  margin-top: 4px;
  font-size: 13px;
  color: var(--color-text-secondary);
  text-align: center;
}

.register-link {
  background: none;
  border: none;
  color: var(--color-accent-primary);
  font-weight: 600;
  cursor: pointer;
  margin-left: 4px;
  padding: 0;
}

.login-footer {
  text-align: center;
  margin-top: 32px;
}

.login-footer p {
  font-size: 12px;
  color: var(--color-text-muted);
  margin: 0;
}

.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 14px;
  z-index: 2000;
}

.toast.success {
  border: 1px solid #10b981;
  color: #10b981;
}

.toast.error {
  border: 1px solid #ef4444;
  color: #ef4444;
}

.toast.warning {
  border: 1px solid #f59e0b;
  color: #f59e0b;
}
</style>
